import {createContext, useCallback, useContext, useEffect, useState} from 'react';
import LocalDB from 'src/utils/localDB';
import {
  authServiceRefresh,
  authServiceVerify,
  userSelfServiceOrgFind,
  userServiceDetail,
} from 'src/apis/autoGenerate/services';
import apiInstance from 'src/apis/config/axiosConfig';
import {useOrgId} from 'src/hooks';
import {AuthRefreshRequest, AuthVerifyResponse} from 'src/apis/autoGenerate/types';
import {useAppDispatch} from 'src/redux/hooks';
import {
  setJwtToken,
  setRole,
  setUserDetail,
  setUserId,
  setOrganization,
} from 'src/redux/slice/accountSlice';

export type StringOrNull = string | null;
interface AuthContextVal {
  token: StringOrNull;
  refreshToken: StringOrNull;
  setToken: (value: StringOrNull) => void;
  setRefreshToken: (value: StringOrNull) => void;
  isLoading: boolean; // Add loading state
}

const AuthContext = createContext<AuthContextVal>({} as AuthContextVal);

export const useAuth = () => {
  return useContext(AuthContext);
};

const setAuthorization = (isSet = true, token?: string) => {
  if (isSet && token) {
    LocalDB.set(LocalDB.alias.token, token);
    apiInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete apiInstance.defaults.headers.common.Authorization;
    LocalDB.remove(LocalDB.alias.token);
    LocalDB.remove(LocalDB.alias.refreshToken);
    LocalDB.remove(LocalDB.alias.sessionId);
    LocalDB.remove(LocalDB.alias.sessionExpiresAt);
  }
};

const AuthProvider = ({children}: {children: JSX.Element}) => {
  const [token, setToken_] = useState<StringOrNull>(LocalDB.get(LocalDB.alias.token));
  const [refreshToken, setRefreshToken_] = useState<StringOrNull>(
    LocalDB.get(LocalDB.alias.refreshToken)
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const {pathname} = window.location;
  const noNeedToken =
    pathname === '/login' ||
    pathname.includes('/setup-password') ||
    pathname.includes('/tenant/accept-invitation');
  const {orgId} = useOrgId();
  const dispatch = useAppDispatch();

  const setToken = useCallback((newToken: StringOrNull) => {
    setToken_(newToken);
    setAuthorization(!!newToken, newToken as string);
  }, []);

  const setRefreshToken = useCallback((newRefreshToken: StringOrNull) => {
    setRefreshToken_(newRefreshToken);
    if (newRefreshToken) {
      LocalDB.set(LocalDB.alias.refreshToken, newRefreshToken);
    } else {
      LocalDB.remove(LocalDB.alias.refreshToken);
    }
  }, []);

  // Verify and refresh token on startup
  useEffect(() => {
    const verifyAuthState = async () => {
      // Skip token verification for public routes
      if (noNeedToken) {
        setIsLoading(false);
        return;
      }

      try {
        if (!token) {
          setIsLoading(false);
          return;
        }

        // Verify token validity
        const verifyResponse = await authServiceVerify({token: token});
        const verifyResult = verifyResponse.data as AuthVerifyResponse;

        if (verifyResult.ok) {
          // Restore user state if not already in Redux
          const userId = LocalDB.get(LocalDB.alias.userId);
          if (userId) {
            dispatch(setUserId(userId));

            // Load user details if not already in Redux
            userServiceDetail(userId).then(res => {
              // Only store the data, not the entire response
              dispatch(setUserDetail(res.data)); // Make sure to only store res.data
            });

            // Restore role if available
            const role = LocalDB.get(LocalDB.alias.role);
            if (role) {
              dispatch(setRole(JSON.parse(role)));
            }

            // Restore organization if available
            const organization = LocalDB.get(LocalDB.alias.organization);
            if (organization) {
              dispatch(setOrganization(JSON.parse(organization)));
            }
          }
        } else {
          // Token is invalid, try to refresh
          if (refreshToken) {
            try {
              // Create the proper refresh request object
              const refreshRequest: AuthRefreshRequest = {
                token: refreshToken,
              };

              const refreshResponse = await authServiceRefresh(refreshRequest);
              const newToken = refreshResponse.data.jwtToken;

              // Update token
              setToken(newToken);
              dispatch(setJwtToken(newToken));
            } catch (refreshError) {
              setToken(null);
              setRefreshToken(null);
            }
          } else {
            setToken(null);
          }
        }
      } catch (error) {
        setToken(null);
      } finally {
        setIsLoading(false);
      }
    };

    verifyAuthState();
  }, [dispatch, noNeedToken, refreshToken, setRefreshToken, setToken, token]);

  // Set organization headers
  useEffect(() => {
    if (orgId) {
      apiInstance.defaults.headers.common['x-org-id'] = orgId;
    }
  }, [orgId]);

  // Fetch organizations when authenticated
  useEffect(() => {
    if (token) {
      userSelfServiceOrgFind()
        .then(res => {
          // 确保 res.data 存在再访问 organizations
          if (res?.data?.organizations) {
            LocalDB.set(LocalDB.alias.organizations, JSON.stringify(res.data.organizations));
          }
        })
        .catch(error => {
          // 改进错误处理，安全地访问错误信息
          const errorMessage =
            error?.response?.data?.error || error?.message || 'Failed to fetch organizations';
          console.error('Error fetching organizations:', errorMessage);

          // 如果是认证错误，清除 token
          if (error?.response?.status === 401) {
            setToken(null);
            setRefreshToken(null);
          }
        });
    }
  }, [token, setToken, setRefreshToken]);

  return (
    <AuthContext.Provider
      value={{
        token,
        refreshToken,
        setToken,
        setRefreshToken,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
